import { lazy } from "react";
import { createBrowserRouter, Navigate } from "react-router";
import { AuthLayout } from "@/layouts/AuthLayout";
import { ProtectedLayout } from "@/layouts/ProtectedLayout";
import { ModuleGuardLayout } from "@/layouts/ModuleGuardLayout";
import { RootLayout } from "@/layouts/RootLayout";
import { LazyPage, RootErrorFallback } from "@/components/ErrorBoundary";
import { LoginPage } from "@/pages/LoginPage";
import { DashboardPage } from "@/pages/DashboardPage";
import { SettingsPage } from "@/pages/SettingsPage";
const LazyTableOrderPage = lazy(() =>
  import("@/pages/TableOrderPage").then((m) => ({
    default: m.TableOrderPage,
  })),
);
import { HelpPage } from "@/pages/HelpPage";
import { ProfilePage } from "@/pages/ProfilePage";

const LazyBillingPage = lazy(() =>
  import("@/pages/BillingPage").then((m) => ({ default: m.BillingPage })),
);

const LazyGroupDashboardPage = lazy(() =>
  import("@/pages/GroupDashboardPage").then((m) => ({
    default: m.GroupDashboardPage,
  })),
);

const LazyInvoicesPage = lazy(() =>
  import("@/pages/InvoicesPage").then((m) => ({
    default: m.InvoicesPage,
  })),
);

const LazySupportDeskPage = lazy(() =>
  import("@/pages/SupportDeskPage").then((m) => ({
    default: m.SupportDeskPage,
  })),
);

const LazyAuditLogPage = lazy(() =>
  import("@/pages/AuditLogPage").then((m) => ({
    default: m.AuditLogPage,
  })),
);

const LazyTicketsPage = lazy(() =>
  import("@/pages/TicketsPage").then((m) => ({
    default: m.TicketsPage,
  })),
);

const LazySupportTicketsPage = lazy(() =>
  import("@/pages/SupportTicketsPage").then((m) => ({
    default: m.SupportTicketsPage,
  })),
);

const LazyStoreOnboardingPage = lazy(() =>
  import("@/pages/StoreOnboardingPage").then((m) => ({
    default: m.StoreOnboardingPage,
  })),
);

const LazySupplierMasterPage = lazy(() =>
  import("@/pages/SupplierMasterPage").then((m) => ({
    default: m.SupplierMasterPage,
  })),
);

const LazyDistributorMasterPage = lazy(() =>
  import("@/pages/DistributorMasterPage").then((m) => ({
    default: m.DistributorMasterPage,
  })),
);

const LazyUserManagementPage = lazy(() =>
  import("@/pages/UserManagementPage").then((m) => ({
    default: m.UserManagementPage,
  })),
);

const LazyShopManagementPage = lazy(() =>
  import("@/pages/ShopManagementPage").then((m) => ({
    default: m.ShopManagementPage,
  })),
);

const LazySchemeMasterPage = lazy(() =>
  import("@/pages/SchemeMasterPage").then((m) => ({
    default: m.SchemeMasterPage,
  })),
);

const LazyWobMasterPage = lazy(() =>
  import("@/pages/WobMasterPage").then((m) => ({
    default: m.WobMasterPage,
  })),
);

const LazyTableOrderConfigPage = lazy(() =>
  import("@/pages/TableOrderConfigPage").then((m) => ({
    default: m.TableOrderConfigPage,
  })),
);

const LazyWaitersPage = lazy(() =>
  import("@/pages/WaitersPage").then((m) => ({
    default: m.WaitersPage,
  })),
);

const LazyMassInvoicePage = lazy(() =>
  import("@/pages/MassInvoicePage").then((m) => ({
    default: m.MassInvoicePage,
  })),
);

const LazyMastersPage = lazy(() =>
  import("@/pages/MastersPage").then((m) => ({
    default: m.MastersPage,
  })),
);

const LazyMassStockTransferPage = lazy(() =>
  import("@/pages/MassStockTransferPage").then((m) => ({
    default: m.MassStockTransferPage,
  })),
);

const LazyGrnAlcoholPage = lazy(() =>
  import("@/pages/GrnAlcoholPage").then((m) => ({
    default: m.GrnAlcoholPage,
  })),
);

const LazyGrnNonAlcoholPage = lazy(() =>
  import("@/pages/GrnNonAlcoholPage").then((m) => ({
    default: m.GrnNonAlcoholPage,
  })),
);

const LazyGrnReturnPage = lazy(() =>
  import("@/pages/GrnReturnPage").then((m) => ({
    default: m.GrnReturnPage,
  })),
);

const LazyGrnNalcReturnPage = lazy(() =>
  import("@/pages/GrnNalcReturnPage").then((m) => ({
    default: m.GrnNalcReturnPage,
  })),
);

const LazyUploadIndentPage = lazy(() =>
  import("@/pages/UploadIndentPage").then((m) => ({
    default: m.UploadIndentPage,
  })),
);

const LazyInventoryPage = lazy(() =>
  import("@/pages/InventoryPage").then((m) => ({
    default: m.InventoryPage,
  })),
);

const LazyStockTransferPage = lazy(() =>
  import("@/pages/StockTransferPage").then((m) => ({
    default: m.StockTransferPage,
  })),
);

const LazyGodownStockPage = lazy(() =>
  import("@/pages/GodownStockPage").then((m) => ({
    default: m.GodownStockPage,
  })),
);

const LazyCashEntryPage = lazy(() =>
  import("@/pages/CashEntryPage").then((m) => ({
    default: m.CashEntryPage,
  })),
);

const LazySupplierBillPayReqPage = lazy(() =>
  import("@/pages/SupplierBillPayReqPage").then((m) => ({
    default: m.SupplierBillPayReqPage,
  })),
);

const LazySupplierBillPayPage = lazy(() =>
  import("@/pages/SupplierBillPayPage").then((m) => ({
    default: m.SupplierBillPayPage,
  })),
);

const LazyPurchasePlanningPage = lazy(() =>
  import("@/pages/PurchasePlanningPage").then((m) => ({
    default: m.PurchasePlanningPage,
  })),
);

const LazyStoreExpensesPage = lazy(() =>
  import("@/pages/StoreExpensesPage").then((m) => ({
    default: m.StoreExpensesPage,
  })),
);

const LazyMassPriceUpdatePage = lazy(() =>
  import("@/pages/MassPriceUpdatePage").then((m) => ({
    default: m.MassPriceUpdatePage,
  })),
);

const LazyMdmPriceUpdatePage = lazy(() =>
  import("@/pages/MdmPriceUpdatePage").then((m) => ({
    default: m.MdmPriceUpdatePage,
  })),
);

const LazySellingPriceMasterPage = lazy(() =>
  import("@/pages/SellingPriceMasterPage").then((m) => ({
    default: m.SellingPriceMasterPage,
  })),
);

const LazyMassInvoiceSalesPage = lazy(() =>
  import("@/pages/MassInvoiceSalesPage").then((m) => ({
    default: m.MassInvoiceSalesPage,
  })),
);

const LazyStoreExpensesCorrectionPage = lazy(() =>
  import("@/pages/StoreExpensesCorrectionPage").then((m) => ({
    default: m.StoreExpensesCorrectionPage,
  })),
);

const LazyAdministrationPage = lazy(() =>
  import("@/pages/AdministrationPage").then((m) => ({
    default: m.AdministrationPage,
  })),
);

const LazyControlManagementPage = lazy(() =>
  import("@/pages/ControlManagementPage").then((m) => ({
    default: m.ControlManagementPage,
  })),
);

const LazyCronJobsPage = lazy(() =>
  import("@/pages/CronJobsPage").then((m) => ({
    default: m.CronJobsPage,
  })),
);

const LazyScriptsPage = lazy(() =>
  import("@/pages/ScriptsPage").then((m) => ({
    default: m.ScriptsPage,
  })),
);

const LazyUtilitiesPage = lazy(() =>
  import("@/pages/UtilitiesPage").then((m) => ({
    default: m.UtilitiesPage,
  })),
);

const LazyCustomReportsPage = lazy(() =>
  import("@/pages/CustomReportsPage").then((m) => ({
    default: m.CustomReportsPage,
  })),
);

const LazyTrackerPage = lazy(() =>
  import("@/pages/TrackerPage").then((m) => ({
    default: m.TrackerPage,
  })),
);

const LazyTransactionsPage = lazy(() =>
  import("@/pages/TransactionsPage").then((m) => ({
    default: m.TransactionsPage,
  })),
);

const LazyTotalSalesPage = lazy(() =>
  import("@/pages/TotalSalesPage").then((m) => ({
    default: m.TotalSalesPage,
  })),
);

const LazyReportsPage = lazy(() =>
  import("@/pages/ReportsPage").then((m) => ({
    default: m.ReportsPage,
  })),
);

const LazyAnalyticsPage = lazy(() =>
  import("@/pages/AnalyticsPage").then((m) => ({
    default: m.AnalyticsPage,
  })),
);

const LazyAnalyticsRegistryPage = lazy(() =>
  import("@/pages/AnalyticsRegistryPage").then((m) => ({
    default: m.AnalyticsRegistryPage,
  })),
);

// Debug page — only loaded in development
const debugRoutes =
  import.meta.env.VITE_APP_ENV === "development"
    ? [
        {
          path: "debug/access",
          lazy: () =>
            import("@/pages/AccessDebugPage").then((m) => ({
              Component: m.AccessDebugPage,
            })),
        },
      ]
    : [];

export const router = createBrowserRouter([
  {
    errorElement: <RootErrorFallback />,
    children: [
      {
        element: <AuthLayout />,
        children: [{ path: "login", element: <LoginPage /> }],
      },
      {
        element: <ProtectedLayout />,
        children: [
          {
            element: <ModuleGuardLayout />,
            children: [
              {
                element: <RootLayout />,
                children: [
                  { index: true, element: <DashboardPage /> },
                  {
                    path: "dashboard",
                    element: <Navigate to="/" replace />,
                  },
                  {
                    path: "billing",
                    element: (
                      <LazyPage>
                        <LazyBillingPage />
                      </LazyPage>
                    ),
                  },
                  {
                    path: "invoices",
                    element: (
                      <LazyPage>
                        <LazyInvoicesPage />
                      </LazyPage>
                    ),
                  },
                  {
                    path: "group-dashboard",
                    element: (
                      <LazyPage>
                        <LazyGroupDashboardPage />
                      </LazyPage>
                    ),
                  },
                  {
                    path: "table-order",
                    element: (
                      <LazyPage>
                        <LazyTableOrderPage />
                      </LazyPage>
                    ),
                  },
                  {
                    path: "support-desk",
                    element: (
                      <LazyPage>
                        <LazySupportDeskPage />
                      </LazyPage>
                    ),
                  },
                  { path: "help", element: <HelpPage /> },
                  { path: "profile", element: <ProfilePage /> },
                  { path: "settings", element: <SettingsPage /> },
                  {
                    path: "audit-log",
                    element: (
                      <LazyPage>
                        <LazyAuditLogPage />
                      </LazyPage>
                    ),
                  },
                  {
                    path: "tickets",
                    element: (
                      <LazyPage>
                        <LazyTicketsPage />
                      </LazyPage>
                    ),
                  },
                  {
                    path: "support-desk/tickets",
                    element: (
                      <LazyPage>
                        <LazySupportTicketsPage />
                      </LazyPage>
                    ),
                  },
                  {
                    path: "store-onboarding",
                    element: (
                      <LazyPage>
                        <LazyStoreOnboardingPage />
                      </LazyPage>
                    ),
                  },
                  {
                    path: "supplier-master",
                    element: (
                      <LazyPage>
                        <LazySupplierMasterPage />
                      </LazyPage>
                    ),
                  },
                  {
                    path: "distributor-master",
                    element: (
                      <LazyPage>
                        <LazyDistributorMasterPage />
                      </LazyPage>
                    ),
                  },
                  {
                    path: "user-management",
                    element: (
                      <LazyPage>
                        <LazyUserManagementPage />
                      </LazyPage>
                    ),
                  },
                  {
                    path: "shop-management",
                    element: (
                      <LazyPage>
                        <LazyShopManagementPage />
                      </LazyPage>
                    ),
                  },
                  {
                    path: "scheme-master",
                    element: (
                      <LazyPage>
                        <LazySchemeMasterPage />
                      </LazyPage>
                    ),
                  },
                  {
                    path: "wob-master",
                    element: (
                      <LazyPage>
                        <LazyWobMasterPage />
                      </LazyPage>
                    ),
                  },
                  {
                    path: "table-order-config",
                    element: (
                      <LazyPage>
                        <LazyTableOrderConfigPage />
                      </LazyPage>
                    ),
                  },
                  {
                    path: "waiters",
                    element: (
                      <LazyPage>
                        <LazyWaitersPage />
                      </LazyPage>
                    ),
                  },
                  {
                    path: "mass-invoice",
                    element: (
                      <LazyPage>
                        <LazyMassInvoicePage />
                      </LazyPage>
                    ),
                  },
                  {
                    path: "masters",
                    element: (
                      <LazyPage>
                        <LazyMastersPage />
                      </LazyPage>
                    ),
                  },
                  {
                    path: "mass-stock-transfer",
                    element: (
                      <LazyPage>
                        <LazyMassStockTransferPage />
                      </LazyPage>
                    ),
                  },
                  {
                    path: "grn-alcohol",
                    element: (
                      <LazyPage>
                        <LazyGrnAlcoholPage />
                      </LazyPage>
                    ),
                  },
                  {
                    path: "grn-nonalcohol",
                    element: (
                      <LazyPage>
                        <LazyGrnNonAlcoholPage />
                      </LazyPage>
                    ),
                  },
                  {
                    path: "grn-return",
                    element: (
                      <LazyPage>
                        <LazyGrnReturnPage />
                      </LazyPage>
                    ),
                  },
                  {
                    path: "grn-nalc-return",
                    element: (
                      <LazyPage>
                        <LazyGrnNalcReturnPage />
                      </LazyPage>
                    ),
                  },
                  {
                    path: "upload-indent",
                    element: (
                      <LazyPage>
                        <LazyUploadIndentPage />
                      </LazyPage>
                    ),
                  },
                  {
                    path: "inventory",
                    element: (
                      <LazyPage>
                        <LazyInventoryPage />
                      </LazyPage>
                    ),
                  },
                  {
                    path: "stock-transfer",
                    element: (
                      <LazyPage>
                        <LazyStockTransferPage />
                      </LazyPage>
                    ),
                  },
                  {
                    path: "godown-stock",
                    element: (
                      <LazyPage>
                        <LazyGodownStockPage />
                      </LazyPage>
                    ),
                  },
                  {
                    path: "cash-entry",
                    element: (
                      <LazyPage>
                        <LazyCashEntryPage />
                      </LazyPage>
                    ),
                  },
                  {
                    path: "supplier-billpay-req",
                    element: (
                      <LazyPage>
                        <LazySupplierBillPayReqPage />
                      </LazyPage>
                    ),
                  },
                  {
                    path: "supplier-billpay",
                    element: (
                      <LazyPage>
                        <LazySupplierBillPayPage />
                      </LazyPage>
                    ),
                  },
                  {
                    path: "purchase-planning",
                    element: (
                      <LazyPage>
                        <LazyPurchasePlanningPage />
                      </LazyPage>
                    ),
                  },
                  {
                    path: "store-expenses",
                    element: (
                      <LazyPage>
                        <LazyStoreExpensesPage />
                      </LazyPage>
                    ),
                  },
                  {
                    path: "mass-price-update",
                    element: (
                      <LazyPage>
                        <LazyMassPriceUpdatePage />
                      </LazyPage>
                    ),
                  },
                  {
                    path: "mdm/price-update",
                    element: (
                      <LazyPage>
                        <LazyMdmPriceUpdatePage />
                      </LazyPage>
                    ),
                  },
                  {
                    path: "selling-price-master",
                    element: (
                      <LazyPage>
                        <LazySellingPriceMasterPage />
                      </LazyPage>
                    ),
                  },
                  {
                    path: "mass-invoice-sales",
                    element: (
                      <LazyPage>
                        <LazyMassInvoiceSalesPage />
                      </LazyPage>
                    ),
                  },
                  {
                    path: "store-expenses-correction",
                    element: (
                      <LazyPage>
                        <LazyStoreExpensesCorrectionPage />
                      </LazyPage>
                    ),
                  },
                  {
                    path: "administration",
                    element: (
                      <LazyPage>
                        <LazyAdministrationPage />
                      </LazyPage>
                    ),
                  },
                  {
                    path: "control-management",
                    element: (
                      <LazyPage>
                        <LazyControlManagementPage />
                      </LazyPage>
                    ),
                  },
                  {
                    path: "cron-jobs",
                    element: (
                      <LazyPage>
                        <LazyCronJobsPage />
                      </LazyPage>
                    ),
                  },
                  {
                    path: "scripts",
                    element: (
                      <LazyPage>
                        <LazyScriptsPage />
                      </LazyPage>
                    ),
                  },
                  {
                    path: "utilities",
                    element: (
                      <LazyPage>
                        <LazyUtilitiesPage />
                      </LazyPage>
                    ),
                  },
                  {
                    path: "custom-reports",
                    element: (
                      <LazyPage>
                        <LazyCustomReportsPage />
                      </LazyPage>
                    ),
                  },
                  {
                    path: "tracker",
                    element: (
                      <LazyPage>
                        <LazyTrackerPage />
                      </LazyPage>
                    ),
                  },
                  {
                    path: "transactions",
                    element: (
                      <LazyPage>
                        <LazyTransactionsPage />
                      </LazyPage>
                    ),
                  },
                  {
                    path: "total-sales",
                    element: (
                      <LazyPage>
                        <LazyTotalSalesPage />
                      </LazyPage>
                    ),
                  },
                  {
                    path: "reports",
                    element: (
                      <LazyPage>
                        <LazyReportsPage />
                      </LazyPage>
                    ),
                  },
                  {
                    path: "analytics",
                    element: (
                      <LazyPage>
                        <LazyAnalyticsPage />
                      </LazyPage>
                    ),
                  },
                  {
                    path: "analytics/registry",
                    element: (
                      <LazyPage>
                        <LazyAnalyticsRegistryPage />
                      </LazyPage>
                    ),
                  },
                  ...debugRoutes,
                ],
              },
            ],
          },
        ],
      },
      { path: "*", element: <Navigate to="/" replace /> },
    ],
  },
]);
